import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { C, LB } from "./theme";
import Icon from "./Icon";
import { useNarrow } from "./useNarrow";
import { useNavigate } from "react-router-dom";
import { FLAG_CODES, NOVELTY_FLAG_CODES, flagName, flagSearchText, flagUrlSquare } from "../../shared/flags";
import { fetchOwnedItems, ownsItem, type OwnedItems } from "../../shared/shop/store";
import { useAuth } from "../../shared/auth";
import { isAdmin, freeEquipEnabled } from "../../shared/admin";
import { ON_PORTAL } from "../../shared/portal-env";

// Pick the country flag your worm flies under. The trigger is a distinct labelled button
// (flag + country name + caret) so it stands out in the player row; clicking it opens a
// searchable grid of named flag cards (a portal popover on desktop, full-screen sheet on
// phones).
const ACCENT = "#37c8e0";
const PANEL_W = 480;
const PANEL_H = 540;

const NOVELTY = new Set(NOVELTY_FLAG_CODES);

// A touch screen gets the phone sheet whatever its width: plenty of phones are
// wider than the 900px breakpoint held sideways, and the desktop popover there
// focused its search field, the keyboard resized the page, and the popover's
// close-on-resize shut it at once.
const touchScreen = () => typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;

export default function FlagPicker({ code, onChange, fill = false }: { code: string; onChange: (code: string) => void; fill?: boolean }) {
  const narrow = useNarrow() || touchScreen();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Novelty flags are shop purchases: load what the player owns when the picker
  // opens, and lock the rest (a locked click leads to the shop).
  const [owned, setOwned] = useState<OwnedItems | null>(null);
  const trigRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return;
    // Phones show the picker as a full-screen sheet, which cannot drift, so none of
    // the drift guards below apply. They would also misfire there: the on-screen
    // keyboard resizes the viewport and scrolls the focused field into view, which
    // used to close the sheet half a second after it opened.
    if (narrow) return;
    const close = () => setOpen(false);
    // Close when the PAGE scrolls (the fixed popover would drift), but NOT when the user
    // scrolls the flag grid inside the popover - so scrolling the list works.
    const onScroll = (e: Event) => {
      const t = e.target as Node | null;
      if (panelRef.current && t && panelRef.current.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("resize", close);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, narrow]);

  const items = useMemo(() => FLAG_CODES.map((c) => ({ code: c, name: flagName(c), search: flagSearchText(c) })).sort((a, b) => a.name.localeCompare(b.name)), []);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) => it.search.includes(s));
  }, [q, items]);

  const openPicker = () => {
    if (!narrow) {
      const r = trigRef.current!.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const flipUp = spaceBelow < PANEL_H + 12 && r.top > spaceBelow;
      const top = Math.round(flipUp ? Math.max(8, r.top - PANEL_H - 6) : r.bottom + 6);
      const left = Math.round(Math.max(8, Math.min(r.left, window.innerWidth - PANEL_W - 8)));
      setPos({ top, left });
    }
    setQ("");
    setOpen(true);
    fetchOwnedItems().then((o) => setOwned(o));
  };
  const pick = (c: string) => { onChange(c); setOpen(false); };

  const header = (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: narrow ? "12px 14px" : "10px 12px", borderBottom: `1px solid ${C.line}`, flex: "0 0 auto" }}>
      <span style={{ fontFamily: LB, fontWeight: 900, fontSize: narrow ? 16 : 14, letterSpacing: 1, flex: 1, color: C.txt }}>CHOOSE YOUR FLAG</span>
      <button type="button" onClick={() => setOpen(false)} className="sx-plain" style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", display: "inline-flex", padding: 0 }}><Icon name="close" size={narrow ? 20 : 18} color={C.muted} /></button>
    </div>
  );

  const search = (
    // Search and the shop link share one row: the search box takes the width,
    // the Shop button is solid accent so it reads as the way to get MORE flags.
    <div style={{ padding: narrow ? "10px 12px 4px" : "8px 10px 4px", flex: "0 0 auto", display: "flex", gap: 8 }}>
      <input
        // Desktop: type straight away. Phones: no autofocus, or the keyboard would
        // spring up over the flag grid before the player has seen it.
        autoFocus={!narrow}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search"
        aria-label="Search flags"
        style={{ flex: 1, minWidth: 0, boxSizing: "border-box", padding: "8px 10px", background: "rgba(255,255,255,0.06)", border: `1px solid ${C.line}`, borderRadius: 8, color: C.txt, fontSize: 14, outline: "none" }}
      />
      {/* Quiet outline button in the app's ghost style: the search box is the
          main thing here, the shop link is a side door. Same height as the
          search field. */}
      {/* (no Store on a game site's build: shared/portal-env.ts) */}
      {!ON_PORTAL && (
        <button
          type="button"
          onClick={() => { setOpen(false); navigate("/shop?tab=flags"); }}
          className="sx-plain"
          title="More flags in the shop"
          style={{ flex: "0 0 auto", alignSelf: "stretch", background: "rgba(40,196,212,0.08)", border: "1px solid rgba(40,196,212,0.45)", color: ACCENT, cursor: "pointer", borderRadius: 8, padding: "0 14px", fontFamily: '"Rajdhani", sans-serif', fontSize: 14, fontWeight: 700, letterSpacing: 0.3, display: "inline-flex", alignItems: "center" }}
        >
          Shop
        </button>
      )}
    </div>
  );

  // Shop (novelty) flags get their own section on top - owned or locked, they
  // are the ones worth showing off - with the countries below under their own
  // heading. Search filters both; an emptied section drops its heading.
  const noveltyFlags = filtered.filter((it) => NOVELTY.has(it.code));
  const countryFlags = filtered.filter((it) => !NOVELTY.has(it.code));

  const sectionHeading = (label: string) => (
    <div style={{ gridColumn: "1 / -1", padding: "6px 2px 1px", fontSize: 11, fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase", color: C.muted }}>{label}</div>
  );

  const flagCell = (it: { code: string; name: string }) => {
    const active = it.code === code;
    // A novelty flag you don't own shows locked (no price here; the shop
    // tells the cost) and leads to the shop instead of equipping. Admins get
    // past the lock only with the Admin page's free-equip toggle on, the same
    // rule as the shop, so an admin account otherwise sees what players see.
    const locked = NOVELTY.has(it.code) && !ownsItem(owned, "flag", it.code) && !(isAdmin(user) && freeEquipEnabled());
    return (
          <button
            key={it.code}
            type="button"
            onClick={() => (locked ? (setOpen(false), navigate(`/shop?tab=flags&confirm=${encodeURIComponent(it.code)}`)) : pick(it.code))}
            title={locked ? `${it.name} · unlock in the shop` : it.name}
            className={"sx-plain flag-cell" + (active ? " flag-cell--active" : "")}
            style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 5, padding: "6px 6px 5px", cursor: "pointer", borderRadius: 8, background: active ? "rgba(55,200,224,0.14)" : "rgba(255,255,255,0.02)", border: `1px solid ${active ? ACCENT : "transparent"}` }}
          >
            <span style={{ position: "relative", width: "100%", aspectRatio: "1 / 1", borderRadius: 4, overflow: "hidden", display: "block" }}>
              <img src={flagUrlSquare(it.code)} alt={it.name} loading="lazy" style={{ display: "block", width: "100%", height: "100%", objectFit: "cover", opacity: locked ? 0.45 : 1 }} />
              {locked ? <span style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", fontSize: 18 }}>🔒</span> : null}
            </span>
            <span style={{ fontSize: 10.5, lineHeight: 1.15, textAlign: "center", color: active ? C.txt : C.muted, maxWidth: "100%", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
              {it.name}
            </span>
            {active ? <span style={{ fontSize: 9, lineHeight: 1, textAlign: "center", color: ACCENT, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase" }}>Equipped</span> : null}
          </button>
    );
  };

  const grid = (
    <div style={{ overflowY: "auto", padding: 8, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(78px, 1fr))", gridAutoRows: "min-content", alignContent: "start", gap: 6, flex: 1, minHeight: 0 }}>
      {noveltyFlags.length > 0 && sectionHeading("Shop Flags")}
      {noveltyFlags.map(flagCell)}
      {countryFlags.length > 0 && sectionHeading("Country Flags")}
      {countryFlags.map(flagCell)}
      {filtered.length === 0 && <div style={{ gridColumn: "1 / -1", color: C.muted, fontSize: 13, padding: 16, textAlign: "center" }}>No flags match &ldquo;{q}&rdquo;.</div>}
    </div>
  );

  return (
    <>
      {/* The trigger is a flag CARD: a square box the flag fills edge to edge. Clean, no
          dropdown chrome. (Square, not stretched to a rectangle: the source flags are drawn
          square, and expanding them to a rectangle distorts them.) */}
      <button
        ref={trigRef}
        type="button"
        onClick={openPicker}
        title={"Flag: " + flagName(code)}
        aria-label={"Flag: " + flagName(code)}
        className="sx-plain flag-card-trigger"
        // (fill: the trigger takes its box, for a host that frames it, e.g. the equipment card)
        style={fill ? { display: "block", width: "100%", height: "100%", padding: 0, cursor: "pointer", border: 0, borderRadius: 0, overflow: "hidden", background: "none" } : { alignSelf: "center", height: 52, width: 52, minWidth: 52, boxSizing: "border-box", padding: 0, cursor: "pointer", borderRadius: 8, overflow: "hidden", border: `1px solid ${C.line}`, background: "rgba(255,255,255,0.05)", flex: "0 0 auto" }}
      >
        <img src={flagUrlSquare(code)} alt="" style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }} />
      </button>
      {open && createPortal(
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 4000, background: narrow ? "rgba(3,6,12,0.72)" : "transparent" }} />
          {narrow ? (
            <div ref={panelRef} style={{ position: "fixed", zIndex: 4001, left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: "min(440px, 94vw)", height: "82vh", display: "flex", flexDirection: "column", background: C.panel, border: `1px solid ${C.line}`, borderRadius: 14, boxShadow: "0 20px 60px rgba(0,0,0,0.6)", color: C.txt }}>
              {header}{search}{grid}
            </div>
          ) : (
            <div ref={panelRef} style={{ position: "fixed", zIndex: 4001, top: pos.top, left: pos.left, width: PANEL_W, height: PANEL_H, boxSizing: "border-box", display: "flex", flexDirection: "column", background: C.panel2, border: `1px solid ${C.line}`, borderRadius: 12, boxShadow: "0 18px 44px rgba(0,0,0,0.6)", color: C.txt }}>
              {header}{search}{grid}
            </div>
          )}
        </>,
        document.body
      )}
    </>
  );
}
